import type { Location } from "@be-miniapp/types"

export const DEFAULT_HCM_LOCATION = {
  latitude: 10.7859311,
  longitude: 106.698644,
} as const satisfies Location

export type LocationSource = "fallback" | "sdk" | "manual"

const isSameCoordinate = (left: unknown, right: number) =>
  typeof left === "number" && Math.abs(left - right) < Number.EPSILON

export function isDefaultHcmLocation(location: unknown): boolean {
  if (!location || typeof location !== "object") return false

  const candidate = location as Partial<Location>
  return (
    isSameCoordinate(candidate.latitude, DEFAULT_HCM_LOCATION.latitude) &&
    isSameCoordinate(candidate.longitude, DEFAULT_HCM_LOCATION.longitude)
  )
}

export function hasFiniteLocation(
  location: Location | null | undefined
): location is Location {
  return (
    Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude)
  )
}

export function resolveHttpRequestPath(
  config?: {
    url?: string
    baseURL?: string
  }
): string | undefined {
  const url = config?.url ?? ""
  const baseURL = config?.baseURL ?? ""

  if (!url && !baseURL) {
    return undefined
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url
  }

  if (baseURL.startsWith("http://") || baseURL.startsWith("https://")) {
    const normalizedBase = baseURL.replace(/\/$/, "")
    const normalizedPath = url.replace(/^\//, "")
    return normalizedPath
      ? `${normalizedBase}/${normalizedPath}`
      : normalizedBase
  }

  return `${baseURL}${url}`
}

export function shouldStripDefaultLocationFromRequest(
  requestUrl: string | undefined
): boolean {
  if (!requestUrl) {
    return false
  }

  // Map/suggestion APIs must not inherit the HCM warmup fallback implicitly.
  return /be-map/i.test(requestUrl)
}

/**
 * Home/configuration APIs need a stable HCM fallback before the user grants
 * location. Map APIs must never receive that fallback implicitly because it
 * would bias suggestions and other location-sensitive results.
 */
export function withoutDefaultLocationForMapRequest<
  T extends Record<string, unknown>,
>(
  additionals: T,
  requestUrl: string | undefined,
  locationSource: LocationSource
): T {
  if (!shouldStripDefaultLocationFromRequest(requestUrl)) return additionals

  const clientInfo =
    additionals.client_info && typeof additionals.client_info === "object"
      ? (additionals.client_info as Record<string, unknown>)
      : undefined

  if (locationSource !== "fallback") return additionals

  const sanitized: Record<string, unknown> = { ...additionals }
  delete sanitized.location
  delete sanitized.latitude
  delete sanitized.longitude

  if (clientInfo) {
    const sanitizedClientInfo = { ...clientInfo }
    delete sanitizedClientInfo.location
    delete sanitizedClientInfo.latitude
    delete sanitizedClientInfo.longitude
    sanitized.client_info = sanitizedClientInfo
  }

  return sanitized as T
}

export function resolveWarmupLocation({
  userLocation,
  persistedLocation,
}: {
  userLocation?: Location | null
  persistedLocation?: Location | null
}): Location {
  if (hasFiniteLocation(userLocation)) {
    return userLocation
  }

  if (hasFiniteLocation(persistedLocation)) {
    return persistedLocation
  }

  return { ...DEFAULT_HCM_LOCATION }
}
